import {api} from '@/helpers/api';
import router from '@/router';
import {getLocalToken, removeLocalToken, saveLocalToken} from '@/utils';
import {AxiosError} from 'axios';
import {getStoreAccessors} from 'typesafe-vuex';
import {ActionContext} from 'vuex';
import {State} from '../state';
import {
  commitAddNotification,
  commitRemoveNotification,
  commitSetAccessLog,
  commitSetAccessLogs,
  commitSetCurrentFormValues,
  commitSetExternalData,
  commitSetForm,
  commitSetFormBatches,
  commitSetForms,
  commitSetFormsFiles,
  commitSetFormsValues,
  commitSetFormValues,
  commitSetLoggedIn,
  commitSetLogInError,
  commitSetMenuForm,
  commitSetMenuForms,
  commitSetNoServerConnection,
  commitSetPrinterLog,
  commitSetPrinterLogs,
  commitSetSearchEngine,
  commitSetToken,
  commitSetUserProfile,
  commitSetUserProfileStats,
} from './mutations';
import {AppNotification, MainState} from './state';
import {IFormBatch, IFormValues} from '@/interfaces/form';
import {IExternalUser} from '@/interfaces/external_user';
import {IEmailTemplate} from '@/interfaces/email-template';
import {IPrinterLog} from '@/interfaces/printer-logs';
import {IAccessLog} from '@/interfaces/access-log';
import {IDynamicQuery} from '@/interfaces';
import {readIsLoggedIn} from "@/store/main/getters";

type MainContext = ActionContext<MainState, State>;

export const actions = {
  async actionRegisterExternalUser(context: MainContext, payload: IExternalUser) {
    try {
      const response = await api.actionRegisterExternalUser(payload);
      if (response) {
        commitAddNotification(context,
            {
              content: 'Uma solicitação de registro foi enviada! Aguarde o email de confirmação.',
              color: 'success',
            });
        return true;
      }
    } catch (err) {
      await dispatchCheckApiError(context, err);
      return false;
    }
  },
  async actionLogIn(context: MainContext, payload: { username: string; password: string }) {
    try {
      const response = await api.logInGetToken(payload.username, payload.password);
      const token = response.data.access_token;
      if (token) {
        saveLocalToken(token);
        commitSetToken(context, token);
        commitSetLoggedIn(context, true);
        commitSetLogInError(context, false);
        commitSetNoServerConnection(context, false);
        await dispatchGetUserProfile(context);
        await dispatchRouteLoggedIn(context);
        commitAddNotification(context, {content: 'Logged in', color: 'success'});
      } else {
        await dispatchLogOut(context);
      }
    } catch (err) {
      if (err.message === 'Network Error') {
        commitSetNoServerConnection(context, true);
      }
      commitSetLogInError(context, true);
      await dispatchLogOut(context);
    }
  },
  async actionGetUserProfile(context: MainContext) {
    try {
      const response = await api.getMe();
      if (response.data) {
        commitSetUserProfile(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetUserProfileStats(context: MainContext) {
    try {
      const response = await api.getMeStats();
      if (response.data) {
        commitSetUserProfileStats(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionUpdateUserProfile(context: MainContext, payload) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.updateMe(context.state.token, payload),
        await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitSetUserProfile(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCheckLoggedIn(context: MainContext) {
    if (!context.state.isLoggedIn) {
      let token = context.state.token;
      if (!token) {
        const localToken = getLocalToken();
        if (localToken) {
          commitSetToken(context, localToken);
          token = localToken;
        }
      }
      if (token) {
        try {
          const response = await api.getMe();
          commitSetLoggedIn(context, true);
          commitSetUserProfile(context, response.data);
        } catch (error) {
          await dispatchRemoveLogIn(context);
          console.error(error)
        }
      } else {
        await dispatchRemoveLogIn(context);
      }
    }
  },
  async actionRemoveLogIn(context: MainContext) {
    removeLocalToken();
    commitSetToken(context, '');
    commitSetLoggedIn(context, false);
  },
  async actionLogOut(context: MainContext) {
    await dispatchRemoveLogIn(context);
    await dispatchRouteLogOut(context);
  },
  async actionUserLogOut(context: MainContext) {
    await dispatchLogOut(context);
    commitAddNotification(context, {content: 'Logged out', color: 'success'});
  },
  actionRouteLogOut(context: MainContext) {
    if (router.currentRoute.path !== '/login') {
      router.push('/login');
      location.reload();
    }
  },
  async actionCheckApiError(context: MainContext, payload: AxiosError) {

    const statusCode = payload?.response?.status
    if (statusCode === 401) {
      await dispatchLogOut(context);
    // @ts-ignore
    } else if ( statusCode >= 400 && statusCode < 499) {
      commitAddNotification(context, {
        color: 'warning', content: `Erro ${payload.response!.status}: ${payload.response!.data}`,
      });
    } else {
      commitAddNotification(context, {
        color: 'error',
        content: `Ocorreu um erro no servidor!
            <br/>Contate o suporte técnico com o código do erro`});
    }

  },
  actionRouteLoggedIn(context: MainContext) {
    if (router.currentRoute.path === '/login' || router.currentRoute.path === '/') {
      router.push('/main');
    }
  },
  async removeNotification(context: MainContext, payload: { notification: AppNotification, timeout: number }) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        commitRemoveNotification(context, payload.notification);
        resolve(true);
      }, payload.timeout);
    });
  },
  async passwordRecovery(context: MainContext, payload: { username: string }) {
    const loadingNotification = {content: 'Sending password recovery email', showProgress: true};
    try {
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.passwordRecovery(payload.username),
        await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      await dispatchLogOut(context);
    } catch (error) {
      await dispatchCheckApiError(context, error);
      // commitRemoveNotification(context, loadingNotification);
      // commitAddNotification(context, {color: 'error', content: 'Incorrect username'});
    }
  },
  async resetPassword(context: MainContext, payload: { password: string, token: string }) {
    const loadingNotification = {content: 'Resetting password', showProgress: true};
    try {
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.resetPassword(payload.password, payload.token),
        await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      await dispatchLogOut(context);
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {color: 'error', content: 'Error resetting password'});
    }
  },

  async actionGetForm(context: MainContext, payload: number) {
    try {
      const response = await api.getForm(payload);
      if (response.data) {
        commitSetForm(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetForms(context: MainContext, payload?: {just_enabled: boolean}) {
    try {
      let response;
      if (payload?.just_enabled) {
        response = await api.getForms(payload);
      } else {
        response = await api.getForms();
      }
      if (response?.data) {
        commitSetForms(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetMenuForm(context: MainContext, payload: number) {
    try {
      const response = await api.getForm(payload);
      if (response.data) {
        commitSetMenuForm(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetMenuForms(context: MainContext) {
    try {
      const response = await api.getForms({just_enabled: true});
      if (response?.data) {
        commitSetMenuForms(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetFormValuesByID(context: MainContext, payload: number) {
    try {
      const response = await api.getFormValuesByID(payload);
      if (response.data) {
        commitSetFormValues(context, response.data);
      }

    } catch (error) {
      const e = error as AxiosError;
      if (e.response?.status === 404) {
        commitSetFormsValues(context, []);
      }
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetFormsValues(context: MainContext, payload: any) {
    try {
      const response = await api.getFormsValues(payload);
      if (response.data) {
        commitSetFormsValues(context, response.data.items);
      }

    } catch (error) {
      const e = error as AxiosError;
      if (e.response?.status === 404) {
        commitSetFormsValues(context, []);
      }
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetFormsValuesWithInterval(context: MainContext, payload: any) {
    try {
      const response = await api.getFormsValuesWithInterval(payload);
      if (response.data) {
        commitSetFormsValues(context, response.data.items);
      }
      return response;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetFormsValuesByQuery(context: MainContext, payload: any) {
    try {
      const response = await api.getFormsValuesByQuery(payload);
      if (response) {
        if (payload?.is_downloaded) {
          const type = response.headers['content-type'];
          // @ts-ignore
          const blob = new Blob([response.data], { type: type, encoding: 'UTF-8' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          const filename = response.headers?.filename;
          link.download = filename ? filename : 'dados_formulario.csv';
          link.click();
        } else {
          commitSetFormsValues(context, response.data.items);
          if (response?.data?.items?.length === 1) {
            await commitSetCurrentFormValues(context, response.data.items[0]);
          } else {
            await commitSetCurrentFormValues(context, {} as IFormValues);
          }
        }
      }
      return response;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionStoreFormsValues(context: MainContext, payload: IFormValues) {
    const loadingNotification = {content: 'Registrando os dados do formulário...', showProgress: true};
    try {
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        await api.registerFormValues(context.state.token, payload),
        await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        color: 'error',
        content: 'Erro ao registrar o formulário. Contate o suporte técnico.',
      });
      return false;
    }
  },
  async actionUpdateFormsValues(context: MainContext, payload: IFormValues) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.updateFormValues(context.state.token, payload),
        await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
      ]))[0];
      // commitSetUserProfile(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteFormsValues(context: MainContext, formValuesId: number) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteFormValues(formValuesId);
      // commitSetUserProfile(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response?.headers?.message, color: 'success'});
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetFormsFile(context: MainContext, payload: number) {
    try {
      const response = await api.getFormsFile(payload);
      if (response.data) {
        commitSetFormsFiles(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetExternalData(context: MainContext, id: number) {
    try {
      const response = await api.getExternalData(id);
      if (response.data) {
        commitSetExternalData(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetSearchEngine(context: MainContext, id: number) {
    try {
      const response = await api.getSearchEngine(id);
      if (response.data) {
        commitSetSearchEngine(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionSentDirectMail(context: MainContext, payload: IEmailTemplate) {
    try {
      const response = await api.sentDirectMail(context.state.token, payload);
      return response;
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  async actionGetFormBatchesAdmin(context: MainContext, payload: { with_stats: boolean, skip: number, limit: number }) {
    try {
      const response = await api.getFormBatchesAdmin(payload);
      if (response) {
        commitSetFormBatches(context, response?.data);
      }
      return response;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetFormBatches(context: MainContext, payload: { with_stats: boolean, skip: number, limit: number }) {
    try {
      const response = await api.getFormBatches(payload);
      if (response) {
        commitSetFormBatches(context, response?.data);
      }
      return response;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionCreateFormBatch(context: MainContext, payload: IFormBatch) {
    try {
      const loadingNotification = {content: 'saving', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.createFormBatch(context.rootState.main.token, payload);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionUpdateFormBatch(context: MainContext, payload: IFormBatch) {
    try {
      const loadingNotification = {content: 'updating', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.updateFormBatch(context.rootState.main.token, payload);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionDeleteFormBatchRequest(context: MainContext, payload: number) {
    try {
      const loadingNotification = {content: 'deleting', showProgress: true};
      commitAddNotification(context, loadingNotification);
      const response = await api.deleteFormBatch(payload);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {content: response.headers.message, color: 'success'});
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionCreatePrinterLog(context: MainContext, payload: IPrinterLog) {
    try {
      const response = await api.createPrinterLog(context.rootState.main.token, payload);
      if (response) {
        await commitSetPrinterLog(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetPrinterLogs(context: MainContext, payload: IDynamicQuery) {
    try {
      const response = await api.getPrinterLogs(payload);
      if (response) {
        commitSetPrinterLogs(context, response.data.items);
      }
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetPrinterLogsValidation(context: MainContext, payload: string) {
    try {
      const response = await api.getPrinterLogsValidation(payload);
      if (response) {
        commitSetPrinterLog(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetAccessLog(context: MainContext, id: number) {
    try {
      const response = await api.getAccessLog(id);
      if (response) {
        commitSetAccessLog(context, response.data);
      }
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionGetAccessLogs(context: MainContext, payload: IDynamicQuery) {
    try {
      const response = await api.getAccessLogs(context.rootState.main.token, payload);
      if (response) {
        commitSetAccessLogs(context, response.data.items);
      }
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

  async actionCreateAccessLog(context: MainContext, payload: IAccessLog) {
    try {
      const response = await api.createAccessLog(context.rootState.main.token, payload);
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },

};

const {dispatch} = getStoreAccessors<MainState | any, State>('');

export const dispatchRegisterExternalUser = dispatch(actions.actionRegisterExternalUser);
export const dispatchCheckApiError = dispatch(actions.actionCheckApiError);
export const dispatchCheckLoggedIn = dispatch(actions.actionCheckLoggedIn);
export const dispatchGetUserProfile = dispatch(actions.actionGetUserProfile);
export const dispatchGetUserProfileStats = dispatch(actions.actionGetUserProfileStats);
export const dispatchLogIn = dispatch(actions.actionLogIn);
export const dispatchLogOut = dispatch(actions.actionLogOut);
export const dispatchUserLogOut = dispatch(actions.actionUserLogOut);
export const dispatchRemoveLogIn = dispatch(actions.actionRemoveLogIn);
export const dispatchRouteLoggedIn = dispatch(actions.actionRouteLoggedIn);
export const dispatchRouteLogOut = dispatch(actions.actionRouteLogOut);
export const dispatchUpdateUserProfile = dispatch(actions.actionUpdateUserProfile);
export const dispatchRemoveNotification = dispatch(actions.removeNotification);
export const dispatchPasswordRecovery = dispatch(actions.passwordRecovery);
export const dispatchResetPassword = dispatch(actions.resetPassword);

export const dispatchGetForm = dispatch(actions.actionGetForm);
export const dispatchGetForms = dispatch(actions.actionGetForms);
export const dispatchGetMenuForm = dispatch(actions.actionGetMenuForm);
export const dispatchGetMenuForms = dispatch(actions.actionGetMenuForms);
export const dispatchGetFormsFile = dispatch(actions.actionGetFormsFile);

export const dispatchGetFormValuesByID = dispatch(actions.actionGetFormValuesByID);
export const dispatchGetFormsValues = dispatch(actions.actionGetFormsValues);
export const dispatchGetFormsValuesByQuery = dispatch(actions.actionGetFormsValuesByQuery);
export const dispatchStoreFormsValues = dispatch(actions.actionStoreFormsValues);
export const dispatchUpdateFormsValues = dispatch(actions.actionUpdateFormsValues);
export const dispatchDeleteFormsValues = dispatch(actions.actionDeleteFormsValues);

export const dispatchGetExternalData = dispatch(actions.actionGetExternalData);

export const dispatchGetSearchEngine = dispatch(actions.actionGetSearchEngine);

export const dispatchSentDirectMail = dispatch(actions.actionSentDirectMail);

export const dispatchGetFormBatches = dispatch(actions.actionGetFormBatches);
export const dispatchGetFormBatchesAdmin = dispatch(actions.actionGetFormBatchesAdmin);
export const dispatchCreateFormBatch = dispatch(actions.actionCreateFormBatch);
export const dispatchUpdateFormBatch = dispatch(actions.actionUpdateFormBatch);
export const dispatchDeleteFormBatch = dispatch(actions.actionDeleteFormBatchRequest);

export const dispatchGetFormsValuesWithInterval = dispatch(actions.actionGetFormsValuesWithInterval);

export const dispatchCreatePrinterLog = dispatch(actions.actionCreatePrinterLog);
export const dispatchGetPrinterLogs = dispatch(actions.actionGetPrinterLogs);
export const dispatchGetPrinterLogsValidation = dispatch(actions.actionGetPrinterLogsValidation);

export const dispatchCreateAccessLog = dispatch(actions.actionCreateAccessLog);
export const dispatchGetAccessLog = dispatch(actions.actionGetAccessLog);
export const dispatchGetAccessLogs = dispatch(actions.actionGetAccessLogs);
